/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useStore } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { useDraftOmsId, usePlaConfigurationsParams } from './hooks';
import { setPageData } from './setPageData';
import isStoreEnabled from '../app/utils/isStoreEnabled';
import { getPlaConfigurationDetails } from '../app/api/plaConfigurationApi';
import {
  setPlaConfigurationDetails,
  setUserHasChangedColor,
} from '../app/actions/plaConfigurations';
import { setHeaderStoreChanged } from '../app/actions/store';
import { getSurfaces } from '../app/actions/skuActions';
import { CountertopsMetadata } from '../app/components/countertopsMetadata/CuntertopsMetadata';
import {
  findPlaColor,
  findPlaSquareFeet,
  findPlaSurfaceCode,
  findPlaSelectedThickness,
  plaDetailPriceResponseFormatter,
  plaConfigurationDetailsFormatter,
  plaPipPricing,
  findPlaProductId,
  waitForAvailableSkus,
} from '../app/utils/plaConfigurationUtils';
import { setPagePlaCanonical } from './setPagePlaCanonical';
import { setGooglePlaData } from './setGooglePlaData';
import logNewRelicCustom, {
  PLA_MARKET_STORE_LOCALIZED,
  PLA_EXPERIENCE_EXPELLED,
  PLA_EXPERIENCE_LANDING,
} from '../app/utils/logNewRelicCustom';
import {
  Utils,
  QUERY_PARAM_BASE_CONFIGURATION_ID_FOR_SELECTIONS,
  QUERY_PARAM_PLA_CONFIGURATION_GUID,
  QUERY_PARAM_REGION_ID,
  POST_FORM_TESTING_PARAM
} from '../app/utils/urlUtils';
import { SET_CURRENT_SKU, SET_LAMINATE_POST_FORM_FEATURE_GATE } from '../app/actions/actionTypes';

import { PipApp } from './PipApp';

export const Pip = ({ history, store, pageData }) => {
  const { storeId, storeZip } = useStore({ varnish: false });
  const { omsId } = useDraftOmsId();
  const { baseConfigurationIdForSelections, plaGuid, regionId } = usePlaConfigurationsParams();
  const windowAvailable = typeof window !== 'undefined' ? window : null;
  const [injectedConfiguratorAvailable, setInjectedConfiguratorAvailable] = useState(false);
  const [surfaceCodes, setSurfaceCodes] = useState(null);
  const [url, setUrl] = useState(null);
  const plaParams = [
    QUERY_PARAM_BASE_CONFIGURATION_ID_FOR_SELECTIONS,
    QUERY_PARAM_PLA_CONFIGURATION_GUID,
    QUERY_PARAM_REGION_ID,
  ];

  useEffect(() => {
    setPageData(pageData);
  }, []);

  useEffect(() => {
    if (windowAvailable) {
      if (windowAvailable?.injectedConfiguratorSettings && !injectedConfiguratorAvailable) {
        setInjectedConfiguratorAvailable(!!windowAvailable?.injectedConfiguratorSettings);
      }

      if (windowAvailable?.injectedConfiguratorSettings && !surfaceCodes) {
        setSurfaceCodes(
        windowAvailable?.injectedConfiguratorSettings?.attributes['external IDs (multiple)']
        );
      }

      if (windowAvailable?.injectedConfiguratorSettings) {
        const laminatePostFormFeatureGate = windowAvailable?.injectedConfiguratorSettings?.laminatePostFormFeatureGate || Utils.getParameterByName(POST_FORM_TESTING_PARAM) === 'true';
        store.dispatch({
          type: SET_LAMINATE_POST_FORM_FEATURE_GATE,
          laminatePostFormFeatureGate
        });
      }

      if (windowAvailable?.location && url === null) {
        setUrl(windowAvailable?.location.href);
      }
    }
  }, [windowAvailable?.injectedConfiguratorSettings, windowAvailable?.location]);

  useEffect(() => {
    if (omsId) {
      store.dispatch(setUserHasChangedColor(null, false));
      if (baseConfigurationIdForSelections && plaGuid && regionId) {
        setPagePlaCanonical();
      }

      const loadPlaConfigurationDetails = async () => {
        let plaConfigurationDetails = await getPlaConfigurationDetails(
          plaGuid,
          baseConfigurationIdForSelections,
          omsId,
          regionId,
          surfaceCodes
        );

        const invalidConfigurationsStructure = Array.isArray(
          plaConfigurationDetails.configurations
        );
        if (invalidConfigurationsStructure) {
          plaConfigurationDetails = plaConfigurationDetailsFormatter(plaConfigurationDetails);
        }

        const plaSurfaceCode = findPlaSurfaceCode(plaConfigurationDetails);
        const productId = findPlaProductId(plaConfigurationDetails);

        if (plaConfigurationDetails.storeNumbers.includes(storeId.toString())) {
          // TODO this can be removed as soon as the new endpoint is in prod. It only accounts for small window where url could receive old configurations shape
          if (!plaConfigurationDetails.configuration.price) {
            const resolvedAvailableSkus = await waitForAvailableSkus(store);

            const newPricing = await plaPipPricing(
              plaConfigurationDetails,
              storeId,
              Number(plaSurfaceCode),
              productId,
              resolvedAvailableSkus
            );
            plaConfigurationDetails.configuration.price = newPricing;
          }
          const plaConfiguredPrice = plaDetailPriceResponseFormatter(
            plaConfigurationDetails.configuration.price
          );

          setGooglePlaData(plaConfigurationDetails, plaConfiguredPrice);
          const plaColor = findPlaColor(plaConfigurationDetails);
          const plaSquareFootage = findPlaSquareFeet(plaConfigurationDetails);
          const plaThickness = findPlaSelectedThickness(plaConfigurationDetails);

          store.dispatch(
            setPlaConfigurationDetails({
              plaConfigurationDetails,
              plaColor,
              plaConfiguredPrice,
              plaSquareFootage,
            })
          );
          store.dispatch({
            type: SET_CURRENT_SKU,
            sku: Number(plaSurfaceCode),
            height: plaThickness,
          });
          sessionStorage.setItem('defaultPlaStoreNumber', storeId.toString());
        } else {
          const hasDefaultPlaStoreNumber = sessionStorage.getItem('defaultPlaStoreNumber');
          if (hasDefaultPlaStoreNumber) {
            Promise.resolve()
              .then(() => {
                logNewRelicCustom(PLA_EXPERIENCE_EXPELLED, {
                  url,
                  regionId,
                  previousStore: hasDefaultPlaStoreNumber,
                  newLocalizedStore: storeId,
                  plaGuid,
                  configurationGuid: baseConfigurationIdForSelections,
                });
                sessionStorage.removeItem('defaultPlaStoreNumber');
                if (typeof window !== 'undefined') {
                  window.history.replaceState(
                    {},
                    document.title,
                    `${window.location.pathname}${Utils.deleteAddQueryParameters(plaParams, [])}`
                  );
                }
              })
              .then(() => {
                if (typeof window !== 'undefined') {
                  window.location.reload();
                }
              })
              .catch((error) => {
                console.error('An error occurred:', error);
              });
          } else {
            logNewRelicCustom(PLA_MARKET_STORE_LOCALIZED, {
              url,
              regionId,
              previousStore: storeId,
              newLocalizedStore: plaConfigurationDetails.storeNumbers[0],
              plaGuid,
              configurationGuid: baseConfigurationIdForSelections,
            });
            sessionStorage.setItem(
              'defaultPlaStoreNumber',
              plaConfigurationDetails.storeNumbers[0]
            );
            (typeof window !== 'undefined') && window.THD_LOCALIZER_AUTO_INIT.Localizer.localize({
              storeId: plaConfigurationDetails.storeNumbers[0],
            }).then(() => {
              if (typeof window !== 'undefined') {
                window.location.reload();
              }
            });
          }
        }
      };

      if (baseConfigurationIdForSelections && injectedConfiguratorAvailable) {
        logNewRelicCustom(PLA_EXPERIENCE_LANDING, {
          url: (typeof window !== 'undefined') ? window.location.href : '',
          baseConfigurationIdForSelections,
          plaGuid,
          regionId,
          storeId,
          storeZip,
        });
        loadPlaConfigurationDetails();
        // loadComponent();
      }
    } else {
      console.log('productId not set.');
    }
  }, [injectedConfiguratorAvailable]);

  useEffect(() => {
    // Update store in Redux when storeId changes
    console.log('[PIP STORE CHANGE] Native app storeId changed to:', storeId);
    console.log('[PIP STORE CHANGE] Current Redux state before dispatch:', {
      reduxStoreNumber: store.getState().store.storeNumber,
      newStoreId: storeId,
      plaGuid,
      surfaceCodes,
      omsId,
    });

    store.dispatch(
      setHeaderStoreChanged({
        isStoreEnabled: isStoreEnabled(storeId),
        storeId,
        zipcode: storeZip,
      })
    );

    console.log('[PIP STORE CHANGE] Redux dispatch completed, state after dispatch:', {
      reduxStoreNumber: store.getState().store.storeNumber,
    });

    // Defer getSurfaces to next event loop cycle to ensure all state updates complete
    // This is critical for Native app webview where bridge timing varies
    setTimeout(() => {
      const currentState = store.getState();
      console.log('[PIP STORE CHANGE] Inside setTimeout (after event loop):');
      console.log('[PIP STORE CHANGE] Redux storeNumber:', currentState.store.storeNumber);
      console.log('[PIP STORE CHANGE] Parameter storeId:', storeId);
      console.log('[PIP STORE CHANGE] Will call getSurfaces:', !plaGuid && surfaceCodes && omsId);

      if (!plaGuid && surfaceCodes && omsId) {
        console.log('[PIP STORE CHANGE] Calling getSurfaces with storeId:', storeId, 'surfaceCodes:', surfaceCodes);
        store.dispatch(getSurfaces(storeId, surfaceCodes));
      } else {
        console.log('[PIP STORE CHANGE] Skipping getSurfaces - plaGuid:', plaGuid, 'surfaceCodes:', surfaceCodes, 'omsId:', omsId);
      }
    }, 0);
  }, [storeId, surfaceCodes, omsId, plaGuid]);

  return (
    <div>
      {plaGuid ? null : (
        <QueryProvider cacheKey="pip-page-countertops-metadata">
          <CountertopsMetadata itemId={omsId} />
        </QueryProvider>
      )}
      <PipApp />
    </div>
  );
};

Pip.propTypes = {};

Pip.displayName = 'PipPage';

Pip.defaultProps = {
  name: 'gcc-thd-pip',
};
